/**
 * Pending Approvals Page - For managers and HR to approve/reject claims
 */

import { useState, useMemo, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import { reimbursementApi } from '../api/reimbursementApi'
import LoadingPage from '@/shared/components/LoadingPage'
import { useAuth } from '@/core/auth/AuthContext'

const formatCurrency = (amount: number, currency: string = 'IDR') => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
  }).format(amount)
}

type SortField = 'claim_number' | 'employee' | 'period' | 'amount' | 'submitted_at'
type SortDirection = 'asc' | 'desc'

interface SortConfig {
  field: SortField
  direction: SortDirection
}

const SortIcon = ({ field, sortConfig }: { field: SortField; sortConfig: SortConfig }) => {
  if (sortConfig.field !== field) {
    return (
      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    )
  }
  return sortConfig.direction === 'asc' ? (
    <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  ) : (
    <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
}

const PendingApprovalsPage = () => {
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'submitted_at', direction: 'desc' })
  const [searchQuery, setSearchQuery] = useState('')

  // Check if user has access to this page (has direct reports OR is in Finance OR is Admin)
  const hasPageAccess = useMemo(() => {
    if (!currentUser) return false
    const hasDirectReports = (currentUser.direct_reports_count ?? 0) > 0
    const isFinance = currentUser.division_name?.toLowerCase() === 'finance'
    const isAdmin = currentUser.roles?.some(r => r.name === 'admin')
    return hasDirectReports || isFinance || isAdmin
  }, [currentUser])

  // Redirect if user doesn't have access
  useEffect(() => {
    if (currentUser && !hasPageAccess) {
      toast.error('You do not have permission to access this page')
      navigate('/reimbursement')
    }
  }, [currentUser, hasPageAccess, navigate])

  const { data, isLoading, error } = useQuery({
    queryKey: ['pendingReimbursementApprovals'],
    queryFn: reimbursementApi.getPendingApprovals,
    enabled: hasPageAccess,
  })

  const handleSort = (field: SortField) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  const filteredAndSortedClaims = useMemo(() => {
    let claims = data?.data || []

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      claims = claims.filter(claim =>
        claim.claim_number.toLowerCase().includes(query) ||
        claim.title.toLowerCase().includes(query) ||
        claim.user?.full_name?.toLowerCase().includes(query) ||
        claim.user?.department?.toLowerCase().includes(query)
      )
    }

    // Sort
    return [...claims].sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1

      switch (sortConfig.field) {
        case 'claim_number':
          return direction * a.claim_number.localeCompare(b.claim_number)
        case 'employee':
          return direction * (a.user?.full_name || '').localeCompare(b.user?.full_name || '')
        case 'period':
          const periodA = a.period_year * 12 + a.period_month
          const periodB = b.period_year * 12 + b.period_month
          return direction * (periodA - periodB)
        case 'amount':
          return direction * (a.total_amount - b.total_amount)
        case 'submitted_at':
          return direction * (new Date(a.submitted_at || 0).getTime() - new Date(b.submitted_at || 0).getTime())
        default:
          return 0
      }
    })
  }, [data?.data, sortConfig, searchQuery])

  // Stats calculations
  const stats = useMemo(() => {
    const allClaims = data?.data || []
    const totalAmount = allClaims.reduce((sum, c) => sum + c.total_amount, 0)
    const withViolations = allClaims.filter(c => c.has_policy_violations).length
    return {
      totalClaims: allClaims.length,
      totalAmount,
      withViolations
    }
  }, [data?.data])

  // Don't render if user doesn't have access (will redirect)
  if (!hasPageAccess) return <LoadingPage />

  if (isLoading) return <LoadingPage />

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-red-600">Failed to load pending approvals</p>
        </div>
      </div>
    )
  }

  const claims = filteredAndSortedClaims

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Pending Approvals</h1>
        <p className="text-gray-600 mt-1">
          Review and approve reimbursement claims from your team
        </p>
      </div>

      {/* Stats Cards */}
      {stats.totalClaims > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pending Claims</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.totalClaims}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Amount</p>
                <p className="text-2xl font-semibold text-gray-900">{formatCurrency(stats.totalAmount)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Policy Violations</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.withViolations}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      {stats.totalClaims > 0 && (
        <div className="mb-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by claim number, title, employee, or department..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Claims List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {claims.length === 0 ? (
          <div className="text-center py-12">
            {searchQuery ? (
              <>
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="mt-4 text-gray-600">No claims match your search</p>
                <p className="text-sm text-gray-500 mt-1">
                  Try adjusting your search terms
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-4 text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Clear search
                </button>
              </>
            ) : (
              <>
                <svg className="mx-auto h-12 w-12 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="mt-4 text-gray-600">No pending approvals</p>
                <p className="text-sm text-gray-500 mt-1">
                  All reimbursement claims have been reviewed
                </p>
              </>
            )}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('claim_number')}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    Claim
                    <SortIcon field="claim_number" sortConfig={sortConfig} />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('employee')}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    Employee
                    <SortIcon field="employee" sortConfig={sortConfig} />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('period')}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    Period
                    <SortIcon field="period" sortConfig={sortConfig} />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('amount')}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    Amount
                    <SortIcon field="amount" sortConfig={sortConfig} />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('submitted_at')}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    Submitted
                    <SortIcon field="submitted_at" sortConfig={sortConfig} />
                  </button>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {claims.map((claim) => (
                <tr key={claim.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <Link
                        to={`/reimbursement/claims/${claim.id}`}
                        className="text-sm font-medium text-red-600 hover:text-red-800"
                      >
                        {claim.claim_number}
                      </Link>
                      <p className="text-sm text-gray-600">{claim.title}</p>
                      {claim.has_policy_violations && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 mt-1">
                          Has policy violations
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{claim.user?.full_name}</div>
                    <div className="text-xs text-gray-500">{claim.user?.department}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {format(new Date(claim.period_year, claim.period_month - 1), 'MMM yyyy')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(claim.total_amount, claim.currency)}
                    </span>
                    {claim.item_count !== undefined && (
                      <p className="text-xs text-gray-500">
                        {claim.item_count} item{claim.item_count !== 1 ? 's' : ''}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {claim.submitted_at &&
                      format(new Date(claim.submitted_at), 'MMM dd, yyyy')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => navigate(`/reimbursement/claims/${claim.id}`)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors font-medium"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Showing results info when searching */}
      {searchQuery && claims.length > 0 && (
        <div className="mt-4 text-sm text-gray-500">
          Showing {claims.length} of {stats.totalClaims} claims
        </div>
      )}
    </div>
  )
}

export default PendingApprovalsPage
